import React from 'react';
import { useTranslation } from 'react-i18next';
import './AlertDialog.css';

interface AlertDialogAction {
  /** 按钮文本 */
  label: string;
  /** 点击处理函数 */
  onClick: () => void;
  /** 按钮类型，primary为主要按钮，secondary为次要按钮 */
  type?: 'primary' | 'secondary';
}

interface AlertDialogHighlightInfo {
  name?: string;
  type?: string;
  tagText?: string;
  description?: string;
}

interface AlertDialogProps {
  /** 是否显示对话框 */
  visible: boolean;
  /** 对话框标题 */
  title: string;
  /** 对话框内容文本 */
  content: string;
  /** 高亮信息内容，支持字符串或结构化对象 */
  highlightInfo?: string | AlertDialogHighlightInfo;
  /** 操作按钮数组 */
  actions: AlertDialogAction[];
  /** 关闭对话框回调，点击遮罩层时触发 */
  onClose?: () => void;
}

/**
 * 通用警告/提示对话框组件
 * 
 * 用于显示用户提示信息，支持自定义标题、内容、高亮信息和操作按钮
 * 
 * @example
 * <AlertDialog
 *   visible={dialogVisible}
 *   content="已有相同的场号，请重新编号"
 *   highlightInfo="# 001｜客厅｜主角与女友争吵"
 *   actions={[
 *     { label: "好的", onClick: handleOk, type: "primary" },
 *     { label: "跳转编辑", onClick: handleJump, type: "secondary" }
 *   ]}
 *   onClose={handleClose}
 * />
 */
const AlertDialog: React.FC<AlertDialogProps> = ({
  visible,
  title,
  content,
  highlightInfo,
  actions,
  onClose
}) => {
  const { t } = useTranslation();
  if (!visible) {
    return null;
  }

  // 处理遮罩层点击
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <div className="alert-dialog-overlay" onClick={handleOverlayClick}>
      <div className="alert-dialog">
        <div className="alert-dialog-header">
          <h3>{title}</h3>
        </div>
        
        <div className="alert-dialog-content">
          <p>{content}</p>
          {highlightInfo && (
            <div className="alert-dialog-highlight">
              {typeof highlightInfo === 'string' ? (
                highlightInfo
              ) : (
                <div className="alert-dialog-highlight-structured">
                  <div className="alert-dialog-highlight-line1">
                    {highlightInfo.type && (
                      <span className="alert-dialog-type">{highlightInfo.type}{t('common.pipeSeparator')}</span>
                    )}
                    <span className="alert-dialog-name">{highlightInfo.name}</span>
                    {highlightInfo.tagText && (
                      <span className="alert-dialog-tag">{highlightInfo.tagText}</span>
                    )}
                  </div>
                  {highlightInfo.description && (
                    <div className="alert-dialog-highlight-line2">
                      {highlightInfo.description}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="alert-dialog-actions">
          {actions.map((action, index) => (
            <button
              key={index}
              className={`alert-btn alert-btn-${action.type || 'secondary'}`}
              onClick={action.onClick}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AlertDialog;
